import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
  AccessibilityInfo,
  Platform,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated'

/**
 * Bumps a counter so tied components can run a one-shot animation.
 * Use with {@link RipplePopIconHost} / {@link ScaleGlowWrap}.
 */
export function useRipplePopPulse() {
  const [generation, setGeneration] = useState(0)
  const trigger = useCallback(() => {
    setGeneration((value) => value + 1)
  }, [])
  return { generation, trigger }
}

const RIPPLE_DURATION_MS = 480
const GLOW_IN_MS = 100
const GLOW_OUT_MS = 380

function runRipplePopIconMotion(params: {
  iconScale: SharedValue<number>
  rippleScale: SharedValue<number>
  rippleOpacity: SharedValue<number>
}) {
  const { iconScale, rippleScale, rippleOpacity } = params

  rippleScale.value = 0.35
  rippleOpacity.value = 0.5
  rippleScale.value = withTiming(2.35, {
    duration: RIPPLE_DURATION_MS,
    easing: Easing.out(Easing.cubic),
  })
  rippleOpacity.value = withSequence(
    withTiming(0.55, { duration: 90 }),
    withTiming(0, { duration: RIPPLE_DURATION_MS - 90 })
  )

  iconScale.value = withSequence(
    withSpring(1.22, { damping: 9, stiffness: 420, mass: 0.55 }),
    withSpring(1, { damping: 14, stiffness: 260, mass: 0.5 })
  )
}

function runScaleGlowMotion(params: {
  contentScale: SharedValue<number>
  glow: SharedValue<number>
}) {
  const { contentScale, glow } = params

  contentScale.value = withSequence(
    withSpring(1.14, { damping: 10, stiffness: 400, mass: 0.55 }),
    withSpring(1, { damping: 15, stiffness: 260, mass: 0.5 })
  )

  glow.value = 0
  glow.value = withSequence(
    withTiming(1, { duration: GLOW_IN_MS, easing: Easing.out(Easing.quad) }),
    withTiming(0, {
      duration: GLOW_OUT_MS,
      easing: Easing.in(Easing.quad),
    })
  )
}

type RipplePopIconHostProps = {
  /** Increment (e.g. from {@link useRipplePopPulse}) to play animations; `0` skips initial run. */
  generation: number
  accentColor: string
  children: ReactNode
  style?: StyleProp<ViewStyle>
}

/**
 * Expanding ring ripple behind children + elastic scale pop (e.g. icon buttons).
 */
export function RipplePopIconHost({
  generation,
  accentColor,
  children,
  style,
}: RipplePopIconHostProps) {
  const iconScale = useSharedValue(1)
  const rippleScale = useSharedValue(0)
  const rippleOpacity = useSharedValue(0)

  useEffect(() => {
    if (generation === 0) {
      return
    }
    const run = () => {
      void AccessibilityInfo.isReduceMotionEnabled().then((reduceMotion) => {
        if (reduceMotion) {
          return
        }
        runRipplePopIconMotion({ iconScale, rippleScale, rippleOpacity })
      })
    }
    run()
  }, [generation, iconScale, rippleOpacity, rippleScale])

  const rippleStyle = useAnimatedStyle(() => ({
    transform: [{ scale: rippleScale.value }],
    opacity: rippleOpacity.value,
  }))

  const iconPopStyle = useAnimatedStyle(() => ({
    transform: [{ scale: iconScale.value }],
  }))

  return (
    <View style={[styles.iconHost, style]}>
      <Animated.View
        pointerEvents="none"
        style={[styles.rippleRing, { borderColor: accentColor }, rippleStyle]}
      />
      <Animated.View style={[styles.iconPop, iconPopStyle]}>
        {children}
      </Animated.View>
    </View>
  )
}

type ScaleGlowWrapProps = {
  generation: number
  accentColor: string
  children: ReactNode
  style?: StyleProp<ViewStyle>
}

/**
 * Brief scale nudge + shadow glow (e.g. numeric badge or label next to an action).
 */
export function ScaleGlowWrap({
  generation,
  accentColor,
  children,
  style,
}: ScaleGlowWrapProps) {
  const contentScale = useSharedValue(1)
  const glow = useSharedValue(0)

  useEffect(() => {
    if (generation === 0) {
      return
    }
    const run = () => {
      void AccessibilityInfo.isReduceMotionEnabled().then((reduceMotion) => {
        if (reduceMotion) {
          return
        }
        runScaleGlowMotion({ contentScale, glow })
      })
    }
    run()
  }, [generation, glow, contentScale])

  const wrapStyle = useAnimatedStyle(() => ({
    transform: [{ scale: contentScale.value }],
    shadowColor: accentColor,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.45 * glow.value,
    shadowRadius: 12 * glow.value,
    elevation: Platform.OS === 'android' ? 6 * glow.value : 0,
  }))

  return (
    <Animated.View style={[styles.labelWrap, wrapStyle, style]}>
      {children}
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  iconHost: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  rippleRing: {
    position: 'absolute',
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 2,
    top: '50%',
    left: '50%',
    marginLeft: -22,
    marginTop: -22,
  },
  iconPop: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
})
